import React, { useCallback } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import isFunction from 'lodash/isFunction';

// INDEXED DB
import { db } from 'db.js';

// GLOBAL VARIABLES
import { INTERFACE } from 'pathnames.js';

// FUNCTIONS
import {
  convertFileToBlob,
  documentChunkFetch,
  documentFetch,
  getFileType,
  modalFunctions,
  shouldOpenFile
} from 'functions';

// CORE COMPONENTS
import Button from 'core/tools/Button';

// STYLES
import styles from './downloadButton.module.scss';

// MAIN COMPONENT
const DownloadButton = ({
  className,
  file,
  meta,
  meta: {
    fileName
  },
  fieldValue,
  shouldChunkDownload = true,
  shouldViewFile = true,
  uploadID,
  viewArgs
}) => {
  // CALLBACKS
  const downloadFile = useCallback(
    url => {
      if (!url) {
        const blob = new Blob([file], { type: file?.type });
        url = window.URL.createObjectURL(blob);
      }
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    },
    [file, fileName]
  );

  const viewFile = useCallback(
    async ({ url, blob, mimeType: fileType }) => {
      if (blob?.size === 0) {
        modalFunctions.error('Unable to download file.');
        return false;
      }

      const mimeType = fileType || getFileType(fileName);
      if (shouldViewFile && blob && shouldOpenFile(mimeType)) {
        try {
          await db.files.put({
            fileName,
            blob,
            mimeType
          });
          window.open(`/${INTERFACE.viewFile}?fileName=${fileName}`, '_blank');
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(err?.message);
          downloadFile(url);
        }
      } else {
        downloadFile(url);
      }
    },
    [downloadFile, fileName, shouldViewFile]
  );

  const handleClick = useCallback(
    async () => {
      const mimeType = getFileType(fileName);
      if (viewArgs) {
        const { endpoint, ...args } = isFunction(viewArgs) ? viewArgs(uploadID, file, meta, fieldValue) || {} : viewArgs || {};

        if (endpoint) {
          const downloadFn = shouldChunkDownload ? documentChunkFetch : documentFetch;
          downloadFn({
            endpoint,
            mimeType,
            loadingMessage: shouldOpenFile(mimeType) ? 'Opening file' : 'Downloading file',
            errorMessage: 'Unable to download file.',
            onSuccess: viewFile,
            ...args
          });
        } else {
          downloadFile();
        }
      } else {
        const blob = await convertFileToBlob(file);
        viewFile({ blob, mimeType });
      }
    },
    [viewArgs, uploadID, file, meta, fieldValue, shouldChunkDownload, fileName, viewFile, downloadFile]
  )

  // RENDER
  return (
    <Button.Link
      className={clsx(
        className,
        styles.button
      )}
      label={fileName}
      onClick={handleClick}
    />
  )
}

export default DownloadButton;
